import { amountDataToDisplay } from "./utils";
import { ICONS } from "../constants";

const MOBILE_MAX_WIDTH_PX = 768;

export const isMobileScreen = () => {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia === "function") {
    return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH_PX - 0.02}px)`).matches;
  }
  return (window.innerWidth || 0) < MOBILE_MAX_WIDTH_PX;
};

export const buildSettlementGroups = ({ contributors, settlements, settlementType, contributorIdDict }) => {
  const list = Array.isArray(contributors) ? contributors : [];
  const byType = settlements?.[settlementType] || {};

  return list
    .map((c) => {
      const debts = Array.isArray(byType?.[c?.id]) ? byType[c.id] : [];
      const rows = debts
        .map((d) => {
          const target = contributorIdDict?.[d?.target];
          return {
            toId: d?.target,
            toIcon: ICONS?.[target?.icon] || "🐾",
            toName: target?.name || String(d?.target || ""),
            amountCents: d?.amount || 0,
          };
        })
        .filter((r) => r.toName && r.amountCents > 0);

      return {
        fromId: c?.id,
        fromIcon: ICONS?.[c?.icon] || "🐾",
        fromName: c?.name || "",
        rows,
      };
    })
    .filter((g) => g.fromName && g.rows.length > 0);
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });

const _imageCache = new Map();
const loadImageCached = (src) => {
  const key = String(src || "");
  if (!key) return Promise.resolve(null);
  if (_imageCache.has(key)) return _imageCache.get(key);
  const p = loadImage(key).catch(() => null);
  _imageCache.set(key, p);
  return p;
};

const toPngBlob = (canvas) =>
  new Promise((resolve, reject) => {
    if (!canvas?.toBlob) return reject(new Error("canvas.toBlob not supported"));
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error("Failed to encode image"));
      // Some share targets (e.g. WeChat) can be strict about MIME types.
      // In some browsers blob.type can be empty even if we requested "image/png".
      if (!blob.type) {
        resolve(new Blob([blob], { type: "image/png" }));
        return;
      }
      resolve(blob);
    }, "image/png");
  });

const ensureFilenameExt = (filename, extNoDot) => {
  const name = String(filename || "").trim();
  if (!name) return `image.${extNoDot}`;
  const hasExt = /\.[a-z0-9]+$/i.test(name);
  if (hasExt) return name;
  return `${name}.${extNoDot}`;
};

const blobToPngFile = (blob, filename) => {
  const safeName = ensureFilenameExt(filename || "settlements.png", "png");
  try {
    return new File([blob], safeName, { type: "image/png", lastModified: Date.now() });
  } catch (e) {
    // Older browsers may not support File constructor.
    // Return a Blob for download/open fallback paths.
    const b = new Blob([blob], { type: "image/png" });
    b.name = safeName; // best-effort; non-standard but sometimes helpful
    return b;
  }
};

const SCENE_SKY = {
  morning: [
    { stop: 0, color: "#cfe9ff" },
    { stop: 0.6, color: "#fdf2d0" },
    { stop: 1, color: "#ffffff" },
  ],
  noon: [
    { stop: 0, color: "#7dd3fc" },
    { stop: 0.55, color: "#e0f2fe" },
    { stop: 1, color: "#ffffff" },
  ],
  afternoon: [
    { stop: 0, color: "#ffcf9a" },
    { stop: 0.35, color: "#ffd6a5" },
    { stop: 1, color: "#cfe9ff" },
  ],
  night: [
    { stop: 0, color: "#0b1224" },
    { stop: 0.55, color: "#0a1a3a" },
    { stop: 1, color: "#071223" },
  ],
};

const STAR_LAYOUT = [
  { left: "8%", top: "10%", size: 26, glyph: "⭐" },
  { left: "18%", top: "26%", size: 18, glyph: "✦" },
  { left: "28%", top: "12%", size: 20, glyph: "★" },
  { left: "38%", top: "30%", size: 16, glyph: "✧" },
  { left: "46%", top: "16%", size: 22, glyph: "⭐" },
  { left: "56%", top: "8%", size: 17, glyph: "✦" },
  { left: "66%", top: "22%", size: 21, glyph: "★" },
  { left: "76%", top: "12%", size: 15, glyph: "✧" },
  { left: "86%", top: "26%", size: 24, glyph: "⭐" },
  { left: "12%", top: "44%", size: 14, glyph: "✦" },
  { left: "34%", top: "52%", size: 15, glyph: "✧" },
  { left: "58%", top: "46%", size: 14, glyph: "✦" },
  { left: "74%", top: "54%", size: 16, glyph: "★" },
  { left: "92%", top: "44%", size: 14, glyph: "✧" },
];

const pctToPx = (pctStr, total) => {
  const s = String(pctStr || "").trim();
  if (s.endsWith("%")) return (parseFloat(s) / 100) * total;
  return parseFloat(s) || 0;
};

const drawCoverImage = (ctx, img, x, y, w, h, opacity = 1) => {
  if (!img) return;
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;

  const scale = Math.max(w / iw, h / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  const dx = x + (w - dw) / 2;
  const dy = y + (h - dh) / 2;

  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.drawImage(img, dx, dy, dw, dh);
  ctx.restore();
};

const drawSceneBackground = async (ctx, { widthCss, heightCss, variant = "morning", isMobile = true } = {}) => {
  const v = String(variant || "morning");
  const stops = SCENE_SKY[v] || SCENE_SKY.morning;

  const grad = ctx.createLinearGradient(0, 0, 0, heightCss);
  stops.forEach((s) => grad.addColorStop(s.stop, s.color));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, widthCss, heightCss);

  const publicUrl = process.env.PUBLIC_URL || "";
  const mountains = await loadImageCached(`${publicUrl}/assets/mountains.svg`);
  const sun = await loadImageCached(`${publicUrl}/assets/sun.svg`);
  const cloud = await loadImageCached(`${publicUrl}/assets/cloud.svg`);
  const grass = await loadImageCached(`${publicUrl}/assets/grass.svg`);

  // Stars (night)
  if (v === "night") {
    ctx.save();
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.textBaseline = "top";
    ctx.shadowColor = "rgba(120,170,255,0.28)";
    ctx.shadowBlur = 10;
    STAR_LAYOUT.forEach((s) => {
      const x = pctToPx(s.left, widthCss);
      const y = pctToPx(s.top, heightCss);
      ctx.font = `${s.size}px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial`;
      ctx.fillText(s.glyph, x, y);
    });
    ctx.restore();
  }

  // Sun (not night) — apply mobile overrides from Scene.css
  if (v !== "night" && sun) {
    let sunTop = v === "morning" ? 90 : v === "noon" ? 55 : 80;
    let sunSize = v === "morning" ? 110 : v === "noon" ? 120 : 112;
    let sunLeft = v === "morning" ? 70 : v === "noon" ? "46%" : "calc(100% - 200px)";

    if (isMobile) {
      sunTop = 54;
      sunSize = 84;
      if (v === "noon") sunLeft = "58%";
      if (v === "afternoon") sunLeft = "calc(100% - 140px)";
    }

    const x =
      typeof sunLeft === "number"
        ? sunLeft
        : String(sunLeft).includes("calc")
          ? widthCss - (isMobile ? 140 : 200)
          : pctToPx(sunLeft, widthCss);
    const y = sunTop;

    ctx.save();
    ctx.globalAlpha = 0.95;
    ctx.drawImage(sun, x, y, sunSize, sunSize);
    ctx.restore();
  }

  // Clouds (mobile only draws 1 cloud; night has none)
  if (v !== "night" && cloud) {
    const cloudSize = isMobile ? 120 : 150;
    const x = pctToPx(isMobile ? "8%" : "10%", widthCss);
    const y = pctToPx("5%", heightCss);
    ctx.save();
    ctx.globalAlpha = isMobile ? 0.7 : 0.8;
    ctx.drawImage(cloud, x, y, cloudSize, cloudSize * 0.6);
    ctx.restore();
  }

  // Mountains at bottom
  if (mountains) {
    const mh = heightCss * (isMobile ? 0.22 : 0.3);
    const opacity = v === "night" ? 0.38 : isMobile ? 0.52 : 0.6;
    drawCoverImage(ctx, mountains, 0, heightCss - mh, widthCss, mh, opacity);
  }

  // Grass patches at bottom (mobile shows 2)
  if (grass) {
    const patches = isMobile
      ? [
          { left: "8%", scale: 1 },
          { left: "32%", scale: 1 },
        ]
      : [
          { left: "8%", scale: 1 },
          { left: "32%", scale: 1.2 },
          { left: "55%", scale: 1 },
          { left: "80%", scale: 0.8 },
        ];
    patches.forEach((p) => {
      const size = 60 * (p.scale || 1);
      const x = pctToPx(p.left, widthCss);
      const y = heightCss - size;
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.drawImage(grass, x, y, size, size);
      ctx.restore();
    });
  }
};

const truncateText = (ctx, text, maxWidth) => {
  const s = String(text || "");
  if (ctx.measureText(s).width <= maxWidth) return s;
  const ell = "…";
  let lo = 0;
  let hi = s.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const candidate = s.slice(0, mid) + ell;
    if (ctx.measureText(candidate).width <= maxWidth) lo = mid + 1;
    else hi = mid;
  }
  const cut = Math.max(0, lo - 1);
  return s.slice(0, cut) + ell;
};

export const renderSettlementsShareCanvas = async ({
  groupName,
  groupId,
  settlementGroups,
  widthCssPx,
  logoSrc,
  owesLabel = "owes",
  sceneVariant,
  theme = {},
}) => {
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  const widthCss = Math.max(320, Math.min(520, Number(widthCssPx) || 390));
  const width = Math.round(widthCss * dpr);

  const isNight = String(sceneVariant || "").toLowerCase() === "night";

  const colors = {
    // background now comes from Scene, but keep these for fallbacks/overrides
    bgTop: theme.bgTop || "#F7FAFF",
    bgBottom: theme.bgBottom || "#FFFFFF",
    // Cards are fully transparent now (Landing-style)
    card: theme.card || "rgba(255, 255, 255, 0)",
    // Text colors follow Landing's readability rule:
    // - night: white
    // - day: black
    ink: theme.ink || (isNight ? "rgba(255, 255, 255, 0.96)" : "rgba(0, 0, 0, 0.86)"),
    muted: theme.muted || (isNight ? "rgba(255, 255, 255, 0.78)" : "rgba(0, 0, 0, 0.62)"),
    line: theme.line || (isNight ? "rgba(255, 255, 255, 0.22)" : "rgba(0, 0, 0, 0.10)"),
  };

  // Layout (CSS px)
  const pad = 20;
  const footerH = 54;
  const sectionGap = 14;

  const headerX = pad;
  const headerY = pad;
  const headerW = widthCss - pad * 2;

  // Logo (optional)
  let logo = null;
  try {
    if (logoSrc) logo = await loadImage(logoSrc);
  } catch (e) {
    // ignore logo failures; the share image should still render
  }

  const nameLine = groupName?.trim() ? groupName.trim() : (groupId ? `Event ${groupId}` : "Event");
  const generatedAt = new Date();
  const generatedLine = `${generatedAt.toLocaleDateString()}`;

  // Header sizing (logo 70% width, 15% gap on each side)
  const headerPadTop = 18;
  const headerPadBottom = 16;
  const headerGapAfterLogo = 10;
  const headerGapBetweenLines = 6;
  const nameLineH = 18;
  const generatedLineH = 14;

  let logoW = 0;
  let logoH = 0;
  if (logo) {
    const iw = logo.naturalWidth || logo.width;
    const ih = logo.naturalHeight || logo.height;
    const targetW = headerW * 0.7;
    const scale = targetW / iw;
    logoW = Math.max(1, Math.round(iw * scale));
    logoH = Math.max(1, Math.round(ih * scale));
  }

  const headerH =
    headerPadTop +
    (logo ? logoH : 0) +
    (logo ? headerGapAfterLogo : 6) +
    nameLineH +
    headerGapBetweenLines +
    generatedLineH +
    headerPadBottom;

  // Visitor Center list card sizing
  const groups = Array.isArray(settlementGroups) ? settlementGroups : [];
  const listPadY = 12;
  const contributorRowH = 44;
  const settlementRowH = 32;
  const listItemPadBottom = 8;
  const listRowDividerH = 1;

  const listContentH =
    groups.reduce(
      (sum, g) =>
        sum + contributorRowH + (Array.isArray(g.rows) ? g.rows.length : 0) * settlementRowH + listItemPadBottom + listRowDividerH,
      0
    ) || 0;

  const listCardMinH = 90;
  const listCardH = Math.max(listCardMinH, listPadY * 2 + listContentH);

  const heightCss = pad + headerH + sectionGap + listCardH + footerH + pad;
  const height = Math.round(heightCss * dpr);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.scale(dpr, dpr);

  // Background: draw the same Scene variant as the page (Option B)
  await drawSceneBackground(ctx, {
    widthCss,
    heightCss,
    variant: sceneVariant || "morning",
    isMobile: true,
  });

  // Header card: fully transparent (Landing-style)

  // Header content (NO "Settlements" title)
  let cursorY = headerY + headerPadTop;
  if (logo) {
    ctx.drawImage(logo, headerX + (headerW - logoW) / 2, cursorY, logoW, logoH);
    cursorY += logoH + headerGapAfterLogo;
  } else {
    cursorY += 6;
  }

  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  ctx.fillStyle = colors.ink;
  ctx.font = "700 16px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
  ctx.fillText(truncateText(ctx, nameLine, headerW - 30), headerX + headerW / 2, cursorY);
  cursorY += nameLineH + headerGapBetweenLines;

  ctx.fillStyle = colors.muted;
  ctx.font = "500 12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
  ctx.fillText(truncateText(ctx, generatedLine, headerW - 30), headerX + headerW / 2, cursorY);

  // List area (Visitor Center style; no edit/delete buttons)
  let y = headerY + headerH + sectionGap;
  const cardX = pad;
  const cardW = widthCss - pad * 2;
  // List card background: fully transparent (Landing-style)

  const drawDivider = (yy) => {
    ctx.strokeStyle = colors.line;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cardX + 12, yy);
    ctx.lineTo(cardX + cardW - 12, yy);
    ctx.stroke();
  };

  const iconX = cardX + 14;
  const nameX = iconX + 34;
  const rightX = cardX + cardW - 14;
  const garnish = "🌿🌼🌱";

  let rowY = y + listPadY;

  if (groups.length === 0) {
    ctx.fillStyle = colors.muted;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "700 14px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
    ctx.fillText("No settlements yet", cardX + cardW / 2, y + Math.min(listCardH, 90) / 2);
  } else {
    groups.forEach((g, idx) => {
      // Contributor row
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";

      ctx.font = "28px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
      ctx.fillStyle = colors.ink;
      ctx.fillText(String(g.fromIcon || "🐾"), iconX, rowY + contributorRowH / 2);

      ctx.font = "700 15px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
      const nameMax = rightX - nameX;
      ctx.fillText(truncateText(ctx, g.fromName, nameMax), nameX, rowY + contributorRowH / 2);

      rowY += contributorRowH;

      const rows = Array.isArray(g.rows) ? g.rows : [];
      rows.forEach((r) => {
        // garnish - measure its width dynamically
        ctx.font = "14px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
        ctx.fillStyle = colors.muted;
        ctx.fillText(garnish, nameX, rowY + settlementRowH / 2);
        const garnishW = ctx.measureText(garnish).width;

        // "owes" - position after garnish with spacing
        const owesX = nameX + garnishW + 8;

        ctx.font = "600 13px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
        ctx.fillStyle = colors.muted;
        const owesText = String(owesLabel || "owes");
        ctx.fillText(owesText, owesX, rowY + settlementRowH / 2);

        const owesW = ctx.measureText(owesText).width;
        const payeeIconX = owesX + owesW + 6;

        ctx.font = "18px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
        ctx.fillStyle = colors.muted;
        ctx.fillText(String(r.toIcon || "🐾"), payeeIconX, rowY + settlementRowH / 2);

        // Measure icon width for better spacing
        const iconW = ctx.measureText(String(r.toIcon || "🐾")).width;
        const textX = payeeIconX + iconW + 6;

        ctx.font = "600 13px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
        ctx.fillStyle = colors.muted;
        const rightText = `${r.toName} $${amountDataToDisplay(r.amountCents)}`;
        const leftMax = rightX - textX;
        ctx.fillText(truncateText(ctx, rightText, leftMax), textX, rowY + settlementRowH / 2);
        rowY += settlementRowH;
      });

      rowY += listItemPadBottom;
      if (idx !== groups.length - 1) {
        drawDivider(rowY);
        rowY += listRowDividerH;
      }
    });
  }

  // Footer
  const footerY = heightCss - pad - footerH;
  ctx.fillStyle = "rgba(21, 34, 56, 0.06)";
  ctx.fillRect(pad, footerY, widthCss - pad * 2, 1);

  ctx.fillStyle = colors.muted;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "600 12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
  ctx.fillText("Made with AnimalSplit • animalsplit.com", widthCss / 2, footerY + 28);

  return canvas;
};

export const shareCanvasImage = async ({ canvas, filename, title, text }) => {
  const blob = await toPngBlob(canvas);
  const file = blobToPngFile(blob, filename || "settlements.png");

  // Prefer native share sheet with image
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text });
        return { didShare: true };
      }
    } catch (e) {
      // fall through to download/open
    }
  }

  // Fallback: open the image in a new tab (best-effort), and also trigger download.
  const url = URL.createObjectURL(blob);
  try {
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (e) {
    // ignore
  }

  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = ensureFilenameExt(filename || "settlements.png", "png");
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  return { didShare: false };
};

